import React, { useState } from "react";
import { Reveal } from "@/components/Reveal";
import { resolveImg } from "@/lib/api";
import { motion } from "framer-motion";
import { X, ArrowRight } from "@phosphor-icons/react";

const EASE = [0.22, 1, 0.36, 1];

// italicise the last word of the headline (e.g. "feel") in the accent colour
function renderTitle(t = "") {
  const m = t.match(/^([\s\S]*?)([A-Za-z']+)([^A-Za-z']*)$/);
  if (!m) return t;
  return (
    <>
      {m[1]}
      <em className="italic text-accent">{m[2]}</em>
      {m[3]}
    </>
  );
}

export default function About({ data, founder, onCta }) {
  const [storyOpen, setStoryOpen] = useState(false);
  const story = (founder?.story || "").trim();
  return (
    <section id="studio" data-testid="about-section" className="bg-background py-24 md:py-40">
      <div className="mx-auto max-w-[1600px] px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-accent">{data?.label}</p>
          </Reveal>
          <Reveal>
            <div className="mt-5 flex items-center justify-center gap-4">
              <span className="h-px w-16 md:w-24" style={{ background: "rgba(150,116,90,0.45)" }} />
              <span className="w-2 h-2 rotate-45" style={{ background: "hsl(var(--accent))" }} />
              <span className="h-px w-16 md:w-24" style={{ background: "rgba(150,116,90,0.45)" }} />
            </div>
          </Reveal>
          <Reveal>
            <h2 className="mt-8 font-serif font-light text-5xl sm:text-6xl md:text-7xl lg:text-[5.25rem] leading-[1.06] tracking-tight">
              {renderTitle(data?.title)}
            </h2>
          </Reveal>
        </div>

        <div className="max-w-6xl mx-auto mt-16 md:mt-24 grid md:grid-cols-2 gap-x-20 gap-y-8">
          <Reveal delay={0.1}>
            <p className="text-lg md:text-xl leading-relaxed text-foreground/70 text-center">{data?.body1}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-lg md:text-xl leading-relaxed text-foreground/70 text-center md:border-l md:border-foreground/15 md:pl-20">{data?.body2}</p>
          </Reveal>
        </div>

        {founder && (
          <div className="relative grid md:grid-cols-12 gap-12 md:gap-16 mt-24 md:mt-36 items-center">
            {/* Photo — whole; slides left & fades as the story opens */}
            <motion.div
              className="md:col-span-6"
              animate={{ x: storyOpen ? -70 : 0, opacity: storyOpen ? 0 : 1 }}
              transition={{ duration: 1, ease: EASE }}
              style={{ pointerEvents: storyOpen ? "none" : "auto" }}
            >
              <Reveal>
                <div className="overflow-hidden">
                  <img
                    src={resolveImg(founder.image)}
                    alt={founder.name}
                    data-testid="founder-photo"
                    className="w-full h-auto object-cover block"
                  />
                </div>
              </Reveal>
            </motion.div>

            {/* Quote — slides right & fades; the toggle below it stays put */}
            <div className="md:col-span-6 md:pl-8 relative z-20">
              <motion.div
                animate={{ x: storyOpen ? 70 : 0, opacity: storyOpen ? 0 : 1 }}
                transition={{ duration: 1, ease: EASE }}
                style={{ pointerEvents: storyOpen ? "none" : "auto" }}
              >
                <Reveal>
                  <blockquote className="font-serif font-light text-3xl sm:text-4xl md:text-5xl leading-[1.15] tracking-tight text-foreground">
                    “{founder.quote}”
                  </blockquote>
                </Reveal>
                <Reveal delay={0.1}>
                  <div className="mt-10">
                    <p className="text-sm uppercase tracking-[0.2em] text-foreground">{founder.name}</p>
                    <p className="mt-1 text-xs uppercase tracking-[0.2em] text-muted-foreground">{founder.role}</p>
                  </div>
                </Reveal>
              </motion.div>

              {story && !storyOpen && (
                <Reveal delay={0.15}>
                  <button
                    type="button"
                    data-testid="founder-read-more-toggle"
                    onClick={() => setStoryOpen(true)}
                    className="mt-8 group inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-foreground hover:text-accent transition-colors duration-500"
                    aria-expanded={storyOpen}
                  >
                    <span className="flex items-center justify-center w-8 h-8 rounded-full border border-foreground/25 group-hover:border-accent transition-colors duration-500">
                      <ArrowRight size={14} weight="light" />
                    </span>
                    Read More
                  </button>
                </Reveal>
              )}
            </div>

            {/* Story — unveiled in the middle as the photo & quote part */}
            {story && (
              <div className="absolute inset-0 z-10 flex items-center justify-center px-4 pointer-events-none">
                <div
                  data-testid="founder-story"
                  className="max-w-xl w-full"
                  style={{ pointerEvents: storyOpen ? "auto" : "none" }}
                >
                  <motion.p
                    animate={{ opacity: storyOpen ? 1 : 0, y: storyOpen ? 0 : 14 }}
                    transition={{ duration: 0.7, ease: EASE, delay: storyOpen ? 0.4 : 0 }}
                    className="text-xs uppercase tracking-[0.3em] text-accent"
                  >
                    {founder.role || "Founder"}
                  </motion.p>
                  <motion.h3
                    animate={{ opacity: storyOpen ? 1 : 0, y: storyOpen ? 0 : 14 }}
                    transition={{ duration: 0.7, ease: EASE, delay: storyOpen ? 0.48 : 0 }}
                    className="mt-3 font-serif font-light text-3xl md:text-4xl tracking-tight text-foreground"
                  >
                    {founder.name}
                  </motion.h3>
                  <motion.span
                    animate={{ opacity: storyOpen ? 1 : 0 }}
                    transition={{ duration: 0.6, ease: EASE, delay: storyOpen ? 0.56 : 0 }}
                    className="block mt-5 h-px w-14 bg-foreground/20"
                  />
                  <div className="mt-6 space-y-4">
                    {story.split(/\n\n+/).map((para, i) => (
                      <motion.p
                        key={i}
                        animate={{ opacity: storyOpen ? 1 : 0, y: storyOpen ? 0 : 16 }}
                        transition={{ duration: 0.8, ease: EASE, delay: storyOpen ? 0.62 + i * 0.12 : 0 }}
                        className="text-sm md:text-base leading-relaxed text-foreground/70"
                      >
                        {para}
                      </motion.p>
                    ))}
                  </div>

                  <motion.button
                    type="button"
                    data-testid="founder-close-toggle"
                    onClick={() => setStoryOpen(false)}
                    animate={{ opacity: storyOpen ? 1 : 0 }}
                    transition={{ duration: 0.6, ease: EASE, delay: storyOpen ? 0.85 : 0 }}
                    className="mt-9 group inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-foreground hover:text-accent transition-colors duration-500"
                  >
                    <span className="flex items-center justify-center w-8 h-8 rounded-full border border-foreground/25 group-hover:border-accent transition-colors duration-500">
                      <X size={13} weight="light" />
                    </span>
                    Close
                  </motion.button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
